import { CdkDragDrop, transferArrayItem } from '@angular/cdk/drag-drop';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { CompatibilityInfoDialogComponent } from '@app/dashboard/dialog/compatibility-info-dialog/compatibility-info-dialog.component';
import { StreetlightsWidgetDialogComponent } from '@app/dashboard/dialog/streetlights-widget-dialog/streetlights-widget-dialog.component';
import { MunicipalityAlarm } from '@app/energy-guard/models/alarms';
import { AlarmsStoreService } from '@app/energy-guard/services/alarms-store.service';
import { AlarmsService } from '@app/energy-guard/services/alarms.service';
import { InformationDataService } from '@app/information/services/information-data.service';
import { SensorTypeDataStoreService } from '@app/sensordaten/services/sensor-type-data-store.service';
import { TermsOfUseDialogComponent } from '@app/shared/dialog/terms-of-use-dialog/terms-of-use-dialog.component';
import { ApplicationInsightEvent, IdpName, PermissionCode, Tenant } from '@app/shared/enums';
import { DashoardProperties } from '@app/shared/models/AppInsights';
import { Disclaimer } from '@app/shared/models/dialog/terms-of-use-dialog-model';
import { DisclaimerService } from '@app/shared/services/api/disclaimer.service';
import { InformationService } from '@app/shared/services/api/information.service';
import { Municipality, MunicipalityService } from '@app/shared/services/api/municipality.service';
import { FeaturePermission, Permission, PermissionDashboardPage, PermissionService } from '@app/shared/services/api/permission.service';
import { UserApplicationService } from '@app/shared/services/api/user-application.service';
import { UserService } from '@app/shared/services/api/user.service';
import { AppInsightsServiceDirective } from '@app/shared/services/app-insights.service';
import { HeaderFooterVisibilityService } from '@app/shared/services/header-footer-visibility.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { StringUtility } from '@app/shared/string-utility';
import { isIE } from '@app/shared/utility';
import { environment } from '@env/environment';
import { isEmpty, isNil } from 'lodash';
import { combineLatest, forkJoin, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, first, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AppInitService } from '../app-initializer.service';
import { AuthService } from '../shared/services/auth.service';
import { MapStoreMunicipality } from '../shared/models/municipality/Municipality';
import { SensorType } from './../sensordaten/models/sensor-type.model';
import { TeaserMainDialogComponent } from './../teaser-main/dialog/teaser-main-dialog/teaser-main-dialog.component';
import { ExistingTeaserPermissionCodes, FEATURE_FUNCTION } from '@app/shared/constants';
import { Information } from '@app/information/models/information';
import { UserApplications, UserApplicationsDto } from '@app/shared/models/dashboard/user-applications';
import { ExterneDataStoreService } from '@app/externe/services/externe-data-store.service';
import { DashboardNotificationService } from './service/dashboard-notification.service';
import { MunicipalityManagementConfigurationStore } from '@app/municipality-management/services/municipality-management-configuration.store';
import { CustomerComplaintCountsStateService } from '@app/information/services/customer-complaint-counts-state.service';
import { InformationUtlity } from '@app/information/information-utility';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DashboardComponent implements OnInit, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  get environment() {
    return environment;
  }
  private compatibilityWindowRef: any;
  private initialAppList: PermissionDashboardPage[] = [];
  private innoLiveUrl: string[] = [];
  private klimaNaviUrl?: string;
  private lokalerUrl?: string;
  private pagePermissionList: Permission[] = [];
  private featurePermissionList: FeaturePermission[] = [];
  private roomMonitorUrl?: string | string[];
  private sensorRouterLink: string[] = [''];
  private userApplications: UserApplications = {} as UserApplications;
  private energieManagementOptimumUrl: string[] = [];
  private externeAppList: PermissionDashboardPage[] = [];
  public myDocsHasNotification = false;
  public displayFavoriteInstruction = false;
  public displayInstruction = false;
  public favoriteAppList: PermissionDashboardPage[] = [];
  public information: Information[] = [];
  public isMunicipalityDeactivated = false;
  public municipalityAlarm: MunicipalityAlarm = {} as MunicipalityAlarm;
  public nonFavoriteAppList: PermissionDashboardPage[] = [];
  public showErrorMessage = false;
  public oneEPColors: boolean = false;

  constructor(
    public appInsightsServiceDirective: AppInsightsServiceDirective,
    public authService: AuthService,
    public matDialog: MatDialog,
    public headerFooterVisibilityService: HeaderFooterVisibilityService,
    public alarmService: AlarmsService,
    public router: Router,
    public overlayLoaderService: OverlayLoaderService,
    public municipalityService: MunicipalityService,
    public alarmStoreService: AlarmsStoreService,
    public sensorTypesStore: SensorTypeDataStoreService,
    public permissionService: PermissionService,
    private readonly snackBar: MatSnackBar,
    private readonly userMunicipalitiesService: UserMunicipalitiesService,
    private readonly userApplicationsService: UserApplicationService,
    private readonly infoService: InformationService,
    private readonly infoDataService: InformationDataService,
    private readonly appInitService: AppInitService,
    private readonly userService: UserService,
    private readonly disclaimerService: DisclaimerService,
    private readonly externeDataStoreService: ExterneDataStoreService,
    private readonly dashboardNotificationService: DashboardNotificationService,
    private readonly municipalityManagementConfigurationStore: MunicipalityManagementConfigurationStore,
    private readonly customerComplaintCountsStateService: CustomerComplaintCountsStateService
  ) {
    this.oneEPColors = StringUtility.areStringsIdentical(environment.tenant, Tenant.LECHWERKE);
  }

  public ngOnInit(): void {
    if (this.municipalityManagementConfigurationStore.updatedExterneUrl) {
      this.municipalityManagementConfigurationStore.updatedExterneUrl = false;
      this.overlayLoaderService.isLoading = true;
      globalThis.location.reload();
    } else {
      this.initializeDashboard();
    }
  }

  public ngOnDestroy(): void {
    this.overlayLoaderService.ignoreChanges = false;
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    if (sessionStorage.getItem('compatibilityWindow') === 'shown') {
      this.closeBrowserCompatibility();
    }
  }

  private initializeDashboard(): void {
    this.setSensorRouterLink();
    this.setMunicipalityAlarm();
    this.setEnergyMonitor();
    this.checkBrowserCompatibility();
    this.setInitialAppList();
    this.customerComplaintCountsStateService.load();
    this.setInformation();
    this.setDisclaimerVersion();
    this.loadUserApplications();
    this.initializeQualtrics();
    this.checkMyDocsNotifications();
  }

  private setSensorRouterLink(): void {
    this.userMunicipalitiesService.currentMunicipality$
      .pipe(
        filter((municipality: MapStoreMunicipality) => !!municipality?.guid && !!municipality?.key),
        distinctUntilChanged(),
        switchMap((municipality: MapStoreMunicipality) => this.sensorTypesStore.refreshSensorTypesObservable(municipality.key)),
        switchMap(() => this.sensorTypesStore.sensorTypes$),
        tap((types: SensorType[]) => {
          this.sensorRouterLink = types.some((t: SensorType) => t.enabled) ? ['/', 'messdaten', 'sensordaten'] : ['/teaser', 'sensordaten'];
        }),
        catchError((e) => this.handleObservableError(e)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }

  private setMunicipalityAlarm(): void {
    this.alarmStoreService.municipalityAlarm$.pipe(takeUntil(this.unsubscribe$)).subscribe((alarm: MunicipalityAlarm) => (this.municipalityAlarm = alarm));
  }

  private setEnergyMonitor(): void {
    this.userMunicipalitiesService.currentMunicipality$
      .pipe(
        filter((municipality: MapStoreMunicipality) => !!municipality?.guid && !!municipality?.key),
        distinctUntilChanged(),
        switchMap((municipality: MapStoreMunicipality) =>
          this.municipalityService.getByMunicipalityGuid(municipality.guid).pipe(
            tap((municipalities: Municipality[]) => {
              this.klimaNaviUrl = municipalities[0].climateNaviUrl;
              this.roomMonitorUrl = municipalities[0].roomMonitoringUrl;
              this.lokalerUrl = municipalities[0].localElectricityMarketUrl;
              this.isMunicipalityDeactivated = municipalities[0].isDeactivated;
            })
          )
        ),
        catchError((e) => this.handleObservableError(e)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }

  private checkBrowserCompatibility(): void {
    this.authService.permissionLoadComplete$
      .asObservable()
      .pipe(
        filter((isLoaded: boolean) => isLoaded),
        catchError((e) => this.handleObservableError(e)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(() => {
        if (isIE() && sessionStorage.getItem('compatibilityWindow') !== 'shown' && this.authService.permissions.COMPATIBILITY_DIALOG_BOX) {
          this.showBrowserCompatibility();
        }
      });
  }

  private setInitialAppList(): void {
    this.initialAppList = this.permissionService.initialPageList(environment.tenant.trim().toLowerCase());
  }

  private setInformation(): void {
    combineLatest([this.infoService.getAll({}), this.customerComplaintCountsStateService.customerComplaintCounts$, this.authService.permissionLoadComplete$])
      .pipe(
        filter(([, , permissionsLoaded]) => permissionsLoaded),
        catchError((e) => this.handleObservableError(e)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(([infos, ,]) => {
        this.information = this.infoDataService.filterActiveRecords(infos).filter((info: Information) => this.shouldDisplayCustomerComplaintPreview(info));
      });
  }

  private setDisclaimerVersion(): void {
    forkJoin([this.userService.getLegalDisclaimerVersionByGuid(this.authService.currentUser.guid), this.disclaimerService.getLatestDisclaimerVersion()])
      .pipe(
        catchError((e) => this.handleObservableError(e)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(([userData, disclaimerData]: [any[], Disclaimer]) => {
        const currentUserDisclaimerVersion = userData[0].legalDisclaimerVersion;
        const latestDisclaimerVersion = disclaimerData.version;
        if (isNil(currentUserDisclaimerVersion) || currentUserDisclaimerVersion !== latestDisclaimerVersion) {
          this.matDialog.open(TermsOfUseDialogComponent, {
            disableClose: true,
            panelClass: ['terms-of-use'],
          });
        }
      });
  }

  private loadUserApplications(): void {
    this.overlayLoaderService.isLoading = true;
    this.overlayLoaderService.ignoreChanges = true;
    forkJoin([this.userApplicationsService.getByUserId(this.authService.currentUser.guid), this.permissionService.getPagePermissons()])
      .pipe(
        catchError((e) => this.handleObservableError(e)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(([userApps, pagePermission]: [UserApplicationsDto, Permission[]]) => {
        const userApp = userApps;
        if (userApp) {
          this.userApplications = this.mapUserApplications(userApp);
        } else {
          this.userApplications = {
            userId: this.authService.currentUser.guid,
            favoritesOrder: [],
            nonFavoritesOrder: [],
          };
        }
        this.pagePermissionList = pagePermission;
        this.initializeDashboardItemList();
      });
  }

  private checkMyDocsNotifications(): void {
    this.dashboardNotificationService.loadApplicationAlerts().subscribe((hasAlerts: boolean) => {
      this.myDocsHasNotification = hasAlerts;
    });
  }

  private initializeQualtrics(): void {
    this.authService.permissionLoadComplete$
      .asObservable()
      .pipe(
        filter((isLoaded: boolean) => isLoaded && !!this.authService.permissions.QUALTRICS),
        catchError((e) => this.handleObservableError(e)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(() => {
        const head = document.getElementsByTagName('head')[0];
        const script = document.createElement('script');
        script.setAttribute('type', 'text/javascript');
        script.src = this.appInitService.getFileLocation('qualtrics.js');
        head.appendChild(script);
        const div = document.createElement('div');
        div.setAttribute('id', this.environment.qualtricsId);
        head.appendChild(div);
      });
  }

  private mapUserApplications(userApp: UserApplicationsDto): UserApplications {
    return {
      id: userApp.id,
      userId: userApp.userId,
      favoritesOrder: userApp.favoritesOrder.split(','),
      nonFavoritesOrder: userApp.nonFavoritesOrder.split(','),
    };
  }

  private initializeDashboardItemList(): void {
    this.userMunicipalitiesService.currentMunicipality$
      .pipe(
        distinctUntilChanged(),
        switchMap(() => {
          return this.authService.permissionLoadComplete$.asObservable().pipe(
            filter((isLoaded: boolean) => isLoaded),
            switchMap(() => {
              return this.getExternalAppFeaturePermission().pipe(
                catchError((e) => {
                  this.handleObservableError(e);
                  return of(null);
                })
              );
            }),
            catchError((e) => this.handleObservableError(e))
          );
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(() => {
        this.favoriteAppList = [];
        this.nonFavoriteAppList = [];
        /* favorites */
        this.userApplications.favoritesOrder.forEach((d: string) => {
          const app = this.initialAppList.find((a) => a.code === d) ?? this.externeAppList.find((a) => a.code === d);
          const permission = this.authService.permissions[d];
          if (app?.featureType?.toLowerCase() === 'e') {
            this.favoriteAppList.push({ ...app });
          } else if (app && permission) {
            this.favoriteAppList.push(this.getAppListItem(app, permission));
          }
        });
        /* non favorites */

        this.userApplications.nonFavoritesOrder.forEach((d: string) => {
          const app = this.initialAppList.find((a) => a.code === d) ?? this.externeAppList.find((a) => a.code === d);
          const permission = this.authService.permissions[d];
          if (app?.featureType?.toLowerCase() === 'e') {
            this.nonFavoriteAppList.push({ ...app });
          } else if (app && permission) {
            this.nonFavoriteAppList.push(this.getAppListItem(app, permission));
          }
        });

        /* force add SOCKET_SERVICE due to widget */
        const socketServiceCode = 'SOCKET_SERVICE';
        if (isNil(this.authService.permissions[socketServiceCode]) && !isNil(this.authService.permissions.STREETLIGHT_PLANNER)) {
          const app = this.initialAppList.find((a: PermissionDashboardPage) => a.code === socketServiceCode);
          if (app) {
            app.isRestricted = false;
            const faveIndex = this.userApplications.favoritesOrder.indexOf(socketServiceCode);
            const nonFaveIndex = this.userApplications.nonFavoritesOrder.indexOf(socketServiceCode);
            if (faveIndex >= 0) {
              this.favoriteAppList.splice(faveIndex, 0, app);
            } else if (nonFaveIndex >= 0) {
              this.nonFavoriteAppList.splice(nonFaveIndex, 0, app);
            }
          }
        }

        /* newly added features for users with existing dashboard order */
        const userSaveList = (this.userApplications.favoritesOrder || []).concat(this.userApplications.nonFavoritesOrder || []);
        const nonMatchingItems = this.initialAppList.filter((a: PermissionDashboardPage) => !userSaveList.includes(a.code));
        nonMatchingItems.forEach((app: PermissionDashboardPage) => {
          const permission = this.authService.permissions[app.code];
          if (permission) {
            this.nonFavoriteAppList.push(this.getAppListItem(app, permission));
          }
        });

        const nonMatchingExterneAppItems = this.externeAppList.filter((a: PermissionDashboardPage) => !userSaveList.includes(a.code));
        nonMatchingExterneAppItems.forEach((app: PermissionDashboardPage) => {
          this.nonFavoriteAppList.push({ ...app });
        });

        this.overlayLoaderService.setLoadingToFalse();
      });
  }

  private getAppListItem(app: PermissionDashboardPage, permission: Record<string, any>): PermissionDashboardPage {
    /* remove is isRestricted, in html as well, all permission codes from authService is isRestricted=false */
    app.isRestricted = false;
    app.isTeaser = permission.teaser;
    /* add to sp and view of get permission to avoid multiple calls */
    app.isPremium = this.pagePermissionList.find((permission: Permission) => permission.code === app.code)?.isPremium || false;
    app.isWarmeDaten = app.code === PermissionCode.WARMEDATEN;
    app.isNewTeaserText = this.showCreateTeaser(app.code);
    return app;
  }

  private showCreateTeaser(code: string): boolean {
    return !StringUtility.hasAnyMatchInArray(ExistingTeaserPermissionCodes, [code]) && !!this.authService.permissions.TEASER_CONTENT_EDITOR;
  }

  public drop(event: CdkDragDrop<any>): void {
    if (!event.container.data && this.favoriteAppList.length < 5) {
      if (event.container.id !== event.previousContainer.id) {
        this.nonFavoriteAppList.splice(event.previousContainer.data.index, 1);
        this.favoriteAppList.push(event.previousContainer.data.item);
      } else {
        this.favoriteAppList.splice(event.previousContainer.data.index, 1);
        this.favoriteAppList.push(event.previousContainer.data.item);
      }
    } else if (!event.container.data && this.nonFavoriteAppList.length < 5) {
      if (event.container.id !== event.previousContainer.id) {
        this.favoriteAppList.splice(event.previousContainer.data.index, 1);
        this.nonFavoriteAppList.push(event.previousContainer.data.item);
      } else {
        this.nonFavoriteAppList.splice(event.previousContainer.data.index, 1);
        this.nonFavoriteAppList.push(event.previousContainer.data.item);
      }
    } else if (event.container.data.container === event.previousContainer.data.container) {
      event.container.data.container[event.previousContainer.data.index] = event.container.data.item;
      event.container.data.container[event.container.data.index] = event.previousContainer.data.item;
    } else {
      if (event.container.id === 'favoriteList' && this.favoriteAppList.length === 5) {
        this.showErrorMessage = true;
        return;
      } else {
        const containerItemIndex = event.container.data.index;
        const previousContainerIndex = event.previousContainer.data.index;

        transferArrayItem(event.previousContainer.data.container, event.container.data.container, previousContainerIndex, containerItemIndex);
      }
    }

    this.updateUserApplications();
  }

  private showBrowserCompatibility(): void {
    const isTenantBag = StringUtility.areStringsIdentical(environment.tenant, Tenant.BAYERNWERK);
    this.compatibilityWindowRef = isTenantBag
      ? this.snackBar.openFromComponent(CompatibilityInfoDialogComponent, {
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['-bayernwerk-compatibility-info'],
        })
      : this.snackBar.openFromComponent(CompatibilityInfoDialogComponent, {
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['-compatibility-info'],
        });
  }

  private closeBrowserCompatibility(): void {
    if (this.compatibilityWindowRef) {
      this.compatibilityWindowRef.dismiss();
    }
  }

  private updateUserApplications(): void {
    const userApplicationsPayload: UserApplicationsDto = {
      ...this.userApplications,
      favoritesOrder: this.favoriteAppList.map((a: PermissionDashboardPage) => a.code).join(','),
      nonFavoritesOrder: this.nonFavoriteAppList.map((a: PermissionDashboardPage) => a.code).join(','),
    };

    this.userApplicationsService
      .save(userApplicationsPayload)
      .pipe(
        catchError((e) => this.handleObservableError(e)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((userApps: UserApplicationsDto) => (this.userApplications = this.mapUserApplications(userApps)));
  }

  public handleClickEvent(code: string, trackingName: string, isTeaser: boolean, item: PermissionDashboardPage): void {
    switch (code) {
      case 'MYDOCS_PAGE':
        this.routeAndTrackUrl(trackingName, ['/documents']);
        break;
      case 'MPM_PAGE':
        this.routeAndTrackUrl(trackingName, ['/lieferstellenverwaltung']);
        break;
      case 'SOLUTION_FINDER':
        this.routeAndTrackUrl(trackingName, ['/loesungsfinder']);
        break;
      case 'ANTRAEGE':
        this.routeAndTrackUrl(trackingName, ['/antraege']);
        break;
      case 'SOCKET_SERVICE':
        this.openStreetlightDialog();
        break;
      case 'CO2_BALANCE_SHEET_LINK':
        this.blockTeaserNavigation(this.klimaNaviUrl as string, trackingName, isTeaser);
        break;
      case 'WEB_SHOP':
        this.openNewTab(environment.webShopUrl, trackingName);
        break;
      case 'EE_NETZNAVI':
        this.openNewTab(environment.eeNetznaviUrl, trackingName);
        break;
      case 'LVN_STREETLIGHT_MANAGER':
        this.openNewTab(environment.lvnStreetlightManagerUrl, trackingName);
        break;
      case 'E_KOMMUNE':
        this.openNewTab(environment.eKommuneUrl, trackingName);
        break;
      case 'METER_READING_LEW':
        this.openNewTab(environment.meterReadingLewUrl, trackingName);
        break;
      case 'SNAP_LVN':
        this.openNewTab(environment.snapLvnUrl, trackingName);
        break;
      case 'ENERGY_REPORT':
        this.openDashboardItem(code, trackingName, isTeaser, ['/energiereport']);
        break;
      case 'PLANAUSKUNFT':
        this.openPlanauskunft(trackingName);
        break;
      case 'PLANAUSKUNFT_LVN':
        this.openNewTab(environment.planauskunftLvn, trackingName);
        break;
      case 'ENERGY_MONITOR':
        this.routeAndTrackUrl('Energiemonitor', ['/energiemonitor']);
        break;
      case 'LOCAL_ELECTRICITY_MARKET_LINK':
        this.blockTeaserNavigation(this.lokalerUrl as string, trackingName, isTeaser);
        break;
      case 'ROOM_MONITORING':
        this.openRoomMonitor(trackingName, isTeaser);
        break;
      case 'SENSOR_DATA':
        this.openSensorData(trackingName, isTeaser);
        break;
      case 'INNO_LIVE':
        this.openInnoLive(trackingName, isTeaser);
        break;
      case 'ENERGY_GUARD':
        this.routeAndTrackUrl(trackingName, ['/energy-guard']);
        break;
      case 'ENERGIE_MANAGEMENT_OPTIMUM':
        this.openOptimum(trackingName, isTeaser);
        break;
      case 'GRID_INFRA':
        this.routeAndTrackUrl(trackingName, ['/grid-infra']);
        break;
      case 'FINANCE_REPORT':
        this.routeAndTrackUrl(trackingName, ['/finanzreport']);
        break;
      default:
        if (item.featureType === 'E') {
          this.openExterneApp(item);
        } else {
          this.trackDashboardLink(trackingName);
        }
        break;
    }
  }

  private openNewTab(url: string, trackingName: string): void {
    this.trackDashboardLink(trackingName);
    if (url) {
      window.open(url, '_blank');
    }
  }

  private openDashboardItem(code: string, trackingName: string, isTeaser: boolean, url: string[]): void {
    this.trackDashboardLink(trackingName);
    isTeaser ? this.launchTeaserDialog(code) : this.routeAndTrackUrl(trackingName, url);
  }

  private storePermissionByIdOrCode(identifier: string): void {
    const combinedList = [...this.pagePermissionList, ...this.featurePermissionList];
    const isGuid = identifier.includes('-');

    const permission = isGuid
      ? combinedList.find((list: Permission) => list.guid === identifier)
      : combinedList.find((list: Permission) => StringUtility.areStringsIdentical(list.code, identifier));

    this.permissionService.selectedPermission$.next({
      id: permission?.guid || '',
      code: permission?.code || identifier,
    });
  }

  private openStreetlightDialog(): void {
    this.matDialog.open(StreetlightsWidgetDialogComponent, {
      panelClass: ['-md', 'streetlights-widget-dialog'],
      width: '1300px',
    });
  }

  private blockTeaserNavigation(url: string, trackingName: string, isTeaser: boolean): void {
    if (isTeaser) {
      return;
    }
    this.openNewTab(url, trackingName);
  }

  private openPlanauskunft(trackingName: string): void {
    this.trackDashboardLink(trackingName);
    if (this.authService.currentUser.idpName.toLocaleUpperCase() === IdpName.CIAM) {
      window.open(environment.planauskunftCiamUrl, '_blank');
    } else {
      window.open(environment.planauskunftPingUrl, '_blank');
    }
  }

  private openRoomMonitor(trackingName: string, isTeaser: boolean): void {
    this.trackDashboardLink(trackingName);
    if (isTeaser) {
      this.roomMonitorUrl = ['/teaser', 'raummonitoring'];
      this.router.navigate(this.roomMonitorUrl);
    } else {
      this.openNewTab(this.roomMonitorUrl as string, trackingName);
    }
  }

  private openSensorData(trackingName: string, isTeaser: boolean): void {
    this.trackDashboardLink(trackingName);
    if (isTeaser) {
      this.sensorRouterLink = ['/teaser', 'sensordaten'];
    }
    this.router.navigate(this.sensorRouterLink);
  }

  private openInnoLive(trackingName: string, isTeaser: boolean): void {
    this.trackDashboardLink(trackingName);
    if (isTeaser) {
      this.innoLiveUrl = ['/inno-live-teaser'];
      this.router.navigate(this.innoLiveUrl);
    } else {
      this.openNewTab(environment.innoLiveUrl, trackingName);
    }
  }

  private trackDashboardLink(link: string): void {
    this.appInsightsServiceDirective.logEvent<DashoardProperties>(ApplicationInsightEvent.clickDashboard, { Link: link });
  }

  private routeAndTrackUrl(trackingName: string, url: string[]): void {
    this.trackDashboardLink(trackingName);
    this.router.navigate(url);
  }

  private openOptimum(trackingName: string, isTeaser: boolean): void {
    this.trackDashboardLink(trackingName);
    if (isTeaser) {
      this.energieManagementOptimumUrl = ['/energie-management-optimum'];
      this.router.navigate(this.energieManagementOptimumUrl);
    } else {
      this.openNewTab(environment.energieManagementOptimumUrl, trackingName);
    }
  }

  private handleObservableError(e: Error): Observable<never> {
    this.overlayLoaderService.setLoadingToFalse();
    return throwError(() => e);
  }

  private getExternalAppFeaturePermission(): Observable<void> {
    const municipalityId = this.userMunicipalitiesService.currentMunicipality.guid;
    return this.permissionService.getFeaturePermissons(municipalityId).pipe(
      first(),
      map((featurePermissions: FeaturePermission[]) => {
        this.externeAppList = [];
        this.mapToPermissionDashboardPages(featurePermissions);
      }),
      takeUntil(this.unsubscribe$)
    );
  }

  private mapToPermissionDashboardPages(featurePermissions: FeaturePermission[]): void {
    featurePermissions.forEach((featurePermission: FeaturePermission) => {
      if (featurePermission.municipalityRestrictions.length > 0 && !featurePermission.municipalityRestrictions[0].isTeaser) {
        return;
      }
      if (featurePermission) {
        const isRestricted = featurePermission.municipalityRestrictions?.length !== 0;
        const isActive = featurePermission.municipalityRestrictions?.length === 0;
        this.featurePermissionList = featurePermissions;
        const externalApp = {
          isActive: isActive,
          guid: featurePermission.guid,
          name: featurePermission.name,
          description: '',
          code: featurePermission.code,
          featureArea: '',
          featureType: 'E',
          featureFunction: featurePermission.featureFunction,
          permission: '',
          isPremium: false,
          isTeaser: featurePermission.municipalityRestrictions[0]?.isTeaser ?? false,
          isAnchor: false,
          isRestricted: isRestricted,
          hoverSrc: '',
          src: featurePermission.iconName,
          class: '',
          title: `${featurePermission.germanName}`,
          clickTrackName: featurePermission.genericExternalURL,
          routerLinkValue: '',
          badgeCount: '',
          isWarmeDaten: false,
          isNewTeaserText: featurePermission.municipalityRestrictions[0]?.isTeaser && this.authService.permissions.TEASER_CONTENT_EDITOR,
          municipalityPermissionsMappings: featurePermission.municipalityPermissionsMappings,
          isTokenRequired: featurePermission.isTokenRequired,
        } as PermissionDashboardPage;

        this.externeAppList.push(externalApp);
      }
    });
  }

  private launchTeaserDialog(identifier: string): void {
    this.storePermissionByIdOrCode(identifier);
    this.matDialog.open(TeaserMainDialogComponent, {
      panelClass: ['ep-generic-dialog'],
    });
  }

  private openExterneApp(item: PermissionDashboardPage): void {
    if (item.isTeaser) {
      this.launchTeaserDialog(item.guid);
      return;
    }

    if (item.featureFunction === FEATURE_FUNCTION.tab) {
      const url = !isEmpty(item.clickTrackName) ? item.clickTrackName : item.municipalityPermissionsMappings?.[0]?.applicationURL;
      this.openNewTab(url, item.title);
    } else {
      this.externeDataStoreService.setExterneData(item);
      this.router.navigate(['/externe']);
    }
  }

  private shouldDisplayCustomerComplaintPreview(info: Information): boolean {
    if (!InformationUtlity.isCustomerComplaintInformation(info)) {
      return true;
    }

    if (this.authService.permissions.DOCUMENT_UPDATE) {
      return true;
    }

    const complaintCount = this.customerComplaintCountsStateService.getByMunicipalityKey(this.userMunicipalitiesService.currentMunicipality?.key ?? '');

    return !!complaintCount;
  }
}
